import WebComponent from '../../webComponents/WebComponent';
import noUiSlider from 'nouislider';
import { makeHistogram } from './histogram';
import wNumb from 'wnumb';
import {dispatchEvent} from '../../lib/dom';

// numberinput helpers
const getNumberOfDecimals = (number) => {
  const numberBag = number.toString().replace(',', '.').split('.');
  const decimals = numberBag[1];
  return decimals ? Math.min(decimals.length, 2) : 0;
};
const normalizeValue = ({ value, step, max, min, defaultValue = 0 }) => {
  let normalizedValue = Number(String(value || defaultValue).replace(",", "."));

  if (min != null && normalizedValue <= min) {
    normalizedValue = Math.max(normalizedValue, min);
  }
  if (max != null && normalizedValue >= max) {
    normalizedValue = Math.min(normalizedValue, max);
  }
  return normalizedValue;
};

const FRAGMENT = {
  histo: '[data-fragment="histo"]',
  component: '[data-fragment="component"]',
  counter: '[data-fragment="counter"]',
  valueField: '[data-fragment="value-field"]',
  inputMin: '[data-fragment="input-min"]',
  inputMax: '[data-fragment="input-max"]',
};

const rangeCounter = (points, min, max) => points
  .filter((el) => ((el[1] > min) || el[1] === max) && el[1] <= max)
  .reduce((acc, el) => acc + el[2], 0);

const findClosest = (values, currentValue) => values.sort((a, b) => Math.abs(currentValue - a) - Math.abs(currentValue - b))[0];

class RangeSlider extends WebComponent {
  constructor(props) {
    super(props);

    const {
      histogram: { min, max, points },
      slider, tooltips, startMin, startMax,
      valueFormat: { suffix, thousandsSep, decimalPoint },
    } = this.props;

    if (points.length === 0) {
      points.push([0, 0, 0]);
      this.props.disabled = true;
    }

    this.decimals = Math.max(...points.map((el) => getNumberOfDecimals(el[1])));
    this.histogramLength = Math.max(...points.map((el) => el[0])) + 1;
    this.filledHistogram = new Array(this.histogramLength).fill().map((_, idx) => {
      const value = points.find((el) => el[0] === idx);
      return [idx, value ? value[1] : 0, value ? value[2] : 0];
    });
    // input data: čísla, která chodí, označují PRAVÉ OKRAJE sloupců!
    this.values = [min, ...points.filter(el => el[2]).map(el => el[1])];
    this.valuesDown = [min, ...points.filter((el, idx, arr) => arr[idx + 1 >= arr.length ? arr.length - 1 : idx + 1][2]).map(el => el[1])];
    this.maxYValue = Math.max(...points.map((point) => point[2]));
    this.step = (max - min) / this.filledHistogram.length;
    this.numberFormat = wNumb({
      decimals: this.decimals,
      mark: decimalPoint,
      thousand: thousandsSep,
    });
    this.labelFormat = wNumb({
      decimals: this.decimals,
      mark: decimalPoint,
      thousand: thousandsSep,
      suffix: ` ${suffix}`,
    });
    this.elementWidth = null;

    // speciální případ pro slider, kde min=max
    const disabledMinEqualsMax = min === max && { min, max: max + 1 };
    if (disabledMinEqualsMax) this.props.disabled = true;
    const disabledMinEqualsMaxFmt = { to: () => `${min} ${suffix}` };

    this.sliderOptions = {
      start: disabledMinEqualsMax
        ? [disabledMinEqualsMax.min, disabledMinEqualsMax.max]
        : [startMin || min, startMax || max],
      range: disabledMinEqualsMax || { min, max },
      tooltips: tooltips ? (disabledMinEqualsMax ? [disabledMinEqualsMaxFmt, disabledMinEqualsMaxFmt] : [true, true]) : [false, false],
      connect: true,
      behaviour: 'tap-drag',
      cssPrefix: 'range-slider__component-',
      format: this.labelFormat,
      ...slider,
    };

    this.state = {
      isExact: (startMin ? this.values.includes(startMin) : true) && (startMax ? this.values.includes(startMax) : true),
      input: {
        values: { min: startMin || min, max: startMax || max },
        limits: {
          inputMin: { min, max },
          inputMax: { min, max },
        },
      },
      formattedInput: {
        min: this.numberFormat.to(startMin || min),
        max: this.numberFormat.to(startMax || max),
      },
      tempInput: null, // při zadávání inputu se sem vkládá nevalidovaný input, zároveň slouží jako kontrola k vystřelení change event
    };
  }

  didMount() {
    const { showHistogram, histogramHeight } = this.props;
    this.sliderElement = this.element.querySelector(FRAGMENT.component);
    this.histogramElement = this.element.querySelector(FRAGMENT.histo);
    this.counter = this.element.querySelector(FRAGMENT.counter);
    this.valueField = this.element.querySelector(FRAGMENT.valueField);
    this.inputMin = this.element.querySelector(FRAGMENT.inputMin);
    this.inputMax = this.element.querySelector(FRAGMENT.inputMax);

    noUiSlider.create(this.sliderElement, this.sliderOptions);
    if (this.sliderElement) {
      this.sliderElement.noUiSlider.on('slide', this.handleSliderChange);
      this.sliderElement.noUiSlider.on('end', this.handleSliderRelease);

      if (showHistogram) {
        this.elementWidth = this.sliderElement.offsetWidth;
        this.histogramElement.style.height = `${histogramHeight * 1.05}px`;
        window.addEventListener('resize', this.handleWindowResize);
      }
    }

    if (!showHistogram && this.counter) this.counter.style.display = 'none';

    if (this.inputMin && this.inputMax) {
      // listenery
      this.selectedInput = undefined;
      [this.inputMin, this.inputMax].forEach((el) => {
        // input se při prvním kliku selectne
        el.addEventListener('focus', () => {
          if (this.selectedInput !== el) {
            el.select();
            this.selectedInput = el;
          }
        });
        el.addEventListener('blur', () => { this.selectedInput = undefined; });

        el.addEventListener('focus', this.handleInputFocus);
        el.addEventListener('blur', this.handleInputBlur);
      });
    }
  }

  handleSliderChange = (vals) => {
    const { input: { limits } } = this.state;
    const newValues = {
      min: findClosest(this.valuesDown, this.numberFormat.from(vals[0])),
      max: findClosest(this.values, this.numberFormat.from(vals[1]), 'down'),
    };
    this.setState({
      input: {
        values: newValues,
        limits,
      },
      formattedInput: {
        min: this.numberFormat.to(newValues.min),
        max: this.numberFormat.to(newValues.max),
      },
      isExact: true,
    });
  }

  handleSliderRelease = () => {
    const [currentMin, currentMax] = this.sliderElement.noUiSlider.get();
    const [closestMin, closestMax] = [
      findClosest(this.valuesDown, this.numberFormat.from(currentMin)),
      findClosest(this.values, this.numberFormat.from(currentMax)),
    ];
    this.sliderElement.noUiSlider.set([closestMin, closestMax]);
    this.update();
    this.dispatchChangeEvent();
  }

  moveSlider = (e) => {
    const { input: { values }, tempInput } = this.state;
    if ((e && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) || !tempInput) {
      if (this.sliderElement) this.sliderElement.noUiSlider.set([values.min, values.max]);
      this.setState({
        isExact: false,
      });
    }
  };

  handleWindowResize = () => {
    if (this.sliderElement) {
      this.elementWidth = this.sliderElement.offsetWidth;
      this.update();
    }
  }

  // input listenery z NumberInput
  handleInputFocus = (e) => {
    if (e) {
      this.inputValue = e.target.value;
    }
    window.addEventListener('keydown', this.handleInputKeyPress);
  };
  handleInputBlur = (e) => {
    if (e) {
      if (this.inputValue != e.target.value) {
        this.handleInputChange(e);
      }
    }
    window.removeEventListener('keydown', this.handleInputKeyPress);
    this.handleInputUpdate();
  };
  handleInputKeyPress = (e) => {
    const { ctrlKey, key } = e;
    const handledInput = e.target.dataset.fragment;
    const floatValidChar = /^[0-9]|,|\.|-$/;
    const integerValidChar = /^[0-9]|-$/;

    if (!ctrlKey) {
      const { values } = this.state.input;
      const value = handledInput === 'input-min' ? values.min : values.max;
      const pattern = this.decimals === 0 ? integerValidChar : floatValidChar;
      const result = pattern.test(key);

      const validKeys = ['ArrowLeft', 'ArrowRight', 'Backspace', 'Delete', 'Tab', 'Enter'];
      if (!result && !validKeys.includes(key)) {
        e.preventDefault();
      }
      // minus only on start
      if (key === '-' && value.length > 0) {
        e.preventDefault();
      }
      // comma or dot only once
      if ((key === '.' || key === ',') && (value.toString().indexOf('.') !== -1 || value.toString().indexOf(',') !== -1)) {
        e.preventDefault();
      }
    }
    if (key === 'ArrowUp') {
      e.preventDefault();
      this.handleInputChange(e, -1);
    }
    if (key === 'ArrowDown') {
      e.preventDefault();
      this.handleInputChange(e, 1);
    }
    if (key === 'Enter') {
      dispatchEvent(e.target, 'blur');
    }
  }
  handleInputChange = (e, inc) => {
    const { input: { values, limits }, formattedInput } = this.state;
    const handledInput = e.target.dataset.fragment;
    const value = inc ? (handledInput === 'input-min' ? values.min : values.max) : e.currentTarget.value;
    const newValue = normalizeValue({
      value: inc ? (inc > 0 ? value - (10 ** -this.decimals) : +value + (10 ** -this.decimals)) : value,
      step: 10 ** -this.decimals,
      min: handledInput === 'input-min' ? limits.inputMin.min : limits.inputMax.min,
      max: handledInput === 'input-min' ? limits.inputMin.max : limits.inputMax.max,
    });

    if (!inc) {
      if (handledInput === 'input-min') this.setState({ formattedInput: { min: value, max: formattedInput.max } });
      else this.setState({ formattedInput: { min: formattedInput.min, max: value } });
    }
    if (inc) {
      if (handledInput === 'input-min') values.min = newValue;
      else values.max = newValue;
      const newFormattedInput = handledInput === 'input-min' ?
        { min: this.numberFormat.to(newValue), max: formattedInput.max } :
        { min: formattedInput.min, max: this.numberFormat.to(newValue) };
      this.setState({ formattedInput: newFormattedInput });
    }

    this.setState({
      tempInput: handledInput === 'input-min' ? { min: newValue, max: values.max } : { min: values.min, max: newValue },
      input: {
        values: inc ? values : handledInput === 'input-min' ? { min: value, max: values.max } : { min: values.min, max: value },
        limits,
      },
    });
    this.handleInputUpdate(inc);
  };

  handleInputUpdate = (inc) => {
    const { tempInput, input: { limits } } = this.state;
    if (tempInput) {
      this.setState({
        input: {
          values: tempInput,
          limits,
        },
        tempInput: null,
        formattedInput: {
          min: this.numberFormat.to(tempInput.min),
          max: this.numberFormat.to(tempInput.max),
        },
      });
    }
    if (!inc) this.moveSlider();
    if (tempInput) {
      this.update();
      this.dispatchChangeEvent();
    }
  }


  update() {
    const { histogram: { points }, disabled, showHistogram } = this.props;
    const { isExact, input: { values, limits }, formattedInput, tempInput } = this.state;
    // mutual input field limits
    if (this.inputMin && this.inputMax && this.sliderOptions) {
      if (limits.inputMin.max !== Number(values.max) && limits.inputMax.min !== Number(values.min)) {
        this.setState({
          input: {
            values,
            limits: {
              inputMin: { min: this.sliderOptions.range.min, max: Number(values.max) },
              inputMax: { min: Number(values.min), max: this.sliderOptions.range.max },
            },
          },
        });
      }
      this.inputMin.value = formattedInput.min;
      this.inputMax.value = formattedInput.max;
    }
    if (this.valueField) this.valueField.value = `${values.min},${values.max}`;

    if (showHistogram && this.histogramElement) {
      const numberOfItems = rangeCounter(points, values.min, values.max);
      const productLabel = (numberOfItems) => {
        if (numberOfItems === 0) return this.i18n('items_5');
        if (numberOfItems === 1) return this.i18n('items');
        if (numberOfItems < 5) return this.i18n('items_2');
        return this.i18n('items_5');
      };

      if (!tempInput) {
        this.histogramElement.innerHTML = makeHistogram(
          this.props,
          this.state.input,
          this.filledHistogram,
          this.histogramLength,
          this.elementWidth,
          this.maxYValue,
          this.decimals,
          this.sliderOptions.range,
        );
      }
      if (this.counter) this.counter.innerHTML = `${isExact ? '' : this.i18n('approx')} ${numberOfItems} ${productLabel(numberOfItems)}`;
    }

    if (disabled) {
      if (this.sliderElement) this.sliderElement.setAttribute('disabled', true);
      if (this.inputMin && this.inputMax) {
        this.inputMin.disabled = true;
        this.inputMax.disabled = true;
      }
    }
  }

  willUnMount() {
    const { showHistogram } = this.state;
    this.sliderElement.noUiSlider.destroy();
    this.sliderElement = undefined;
    this.valueField = undefined;
    this.inputMin = undefined;
    this.inputMax = undefined;
    this.histogramElement = undefined;
    this.counter = undefined;

    window.removeEventListener('keydown', this.handleKeyPress);

    if (showHistogram) window.removeEventListener('resize', this.handleWindowResize);
  }
}

RangeSlider.defaultProps = {
  histogram: { min: 0, max: 0, points: [] },
  showHistogram: true,
  histogramHeight: 200,
  histogramColsBorder: window.innerWidth > 350 ? 1 : 0,
  disabled: false,
  inputMin: null,
  inputMax: null,
  valueFormat: {
    suffix: '',
    thousandsSep: ' ',
    decimalPoint: ',',
  },
  messages: {
    approx: 'zhruba',
    items: 'produkt',
    items_2: 'produkty',
    items_5: 'produktů',
  },
};

export default RangeSlider;



// WEBPACK FOOTER //
// ./node_modules/fg-frontend/src/components/RangeSlider/RangeSlider.js